/**
 * Translation utilities for auto-translating table columns
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// In-memory cache for translations to avoid redundant API calls
const translationCache = new Map();

/**
 * Translate text using DeepL API via backend
 * @param {string} text - Text to translate
 * @param {string} sourceLang - Source language code (e.g., 'NL' for Dutch)
 * @param {string} targetLang - Target language code (e.g., 'EN' for English)
 * @param {string} token - Auth token
 * @returns {Promise<string>} - Translated text
 */
export async function translateText(text, sourceLang = 'NL', targetLang = 'EN', token) {
  // Check cache first
  const cacheKey = `${sourceLang}:${targetLang}:${text}`;
  if (translationCache.has(cacheKey)) {
    console.log('[Translation] Cache hit:', text.substring(0, 30));
    return translationCache.get(cacheKey);
  }

  // Skip empty or whitespace-only text
  if (!text || !text.trim()) {
    return '';
  }

  try {
    console.log('[Translation] Translating:', text.substring(0, 50), `(${sourceLang} → ${targetLang})`);

    const response = await fetch(`${API_URL}/api/translate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        text: text.trim(),
        source_lang: sourceLang,
        target_lang: targetLang
      })
    });

    if (!response.ok) {
      const error = await response.text();
      console.error('[Translation] Error:', response.status, error);
      throw new Error(`Translation failed: ${error}`);
    }

    const data = await response.json();
    const translation = data.translation;

    // Cache the result
    translationCache.set(cacheKey, translation);

    console.log('[Translation] Success:', translation.substring(0, 50), data.cached ? '(from server cache)' : '');

    return translation;
  } catch (error) {
    console.error('[Translation] Failed:', error);
    // Return original text on error
    return text;
  }
}

/**
 * Check if a column is configured for auto-translation
 * @param {object} table - Table data
 * @param {number} colIdx - Column index
 * @returns {object|null} - Translation config or null
 */
export function getColumnTranslationConfig(table, colIdx) {
  if (!table.translation_columns) {
    return null;
  }
  return table.translation_columns[colIdx] || null;
}

/**
 * Set or update translation configuration for a column
 * @param {object} table - Table data
 * @param {number} colIdx - Column index to configure
 * @param {object} config - Translation config: {sourceColumn, sourceLang, targetLang}
 * @returns {object} - Updated table
 */
export function setColumnTranslationConfig(table, colIdx, config) {
  const updatedTable = { ...table };

  if (!updatedTable.translation_columns) {
    updatedTable.translation_columns = {};
  }

  if (config === null) {
    // Remove translation config
    delete updatedTable.translation_columns[colIdx];
  } else {
    // Set translation config
    updatedTable.translation_columns[colIdx] = config;
  }

  return updatedTable;
}

/**
 * Language options for translation
 */
export const LANGUAGES = [
  { code: 'NL', name: 'Dutch' },
  { code: 'EN', name: 'English' },
  { code: 'DE', name: 'German' },
  { code: 'FR', name: 'French' },
  { code: 'ES', name: 'Spanish' },
  { code: 'IT', name: 'Italian' },
  { code: 'PT', name: 'Portuguese' },
  { code: 'PL', name: 'Polish' },
  { code: 'RU', name: 'Russian' },
  { code: 'JA', name: 'Japanese' },
  { code: 'ZH', name: 'Chinese' }
];
